import React, { useState } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import JiraExportProgress from './JiraExportProgress';
import TrelloExportProgress from './TrelloExportProgress';
import { Modal, tokens } from '../design-system';

// Constants for export functionality
const BOARD_NAME_MAX_LENGTH = 30;

const ExportModal = ({
  isOpen,
  onClose,
  task,
  planId,
  features = [],
  onShowToast,
  inline = false
}) => {
  const [isTrelloModalOpen, setIsTrelloModalOpen] = useState(false);
  const [isJiraModalOpen, setIsJiraModalOpen] = useState(false);
  const [isJiraProgressOpen, setIsJiraProgressOpen] = useState(false);
  const [isTrelloProgressOpen, setIsTrelloProgressOpen] = useState(false);
  const [boardName, setBoardName] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [currentExportId, setCurrentExportId] = useState(null);
  const [currentTrelloExportId, setCurrentTrelloExportId] = useState(null);
  const [jiraConfig, setJiraConfig] = useState({
    projectName: '',
    projectKey: '',
    jiraUrl: '',
    apiToken: '',
    userEmail: '',
    accountId: '',
    users: [{ username: '', email: '' }]
  });
  const { settings } = useSettings();

  const onTrelloModalOpen = () => {
    // Pre-fill board name from task description
    if (task?.description) {
      setBoardName(`Cahoots: ${task?.description?.split('\n')[0].substring(0, BOARD_NAME_MAX_LENGTH)}`);
    }
    setIsTrelloModalOpen(true);
  };
  const onTrelloModalClose = () => setIsTrelloModalOpen(false);
  const onJiraModalOpen = () => {
    // Always show the modal for project configuration, but pre-fill credentials if available
    setJiraConfig({
      projectName: task?.description ? `Cahoots: ${task?.description?.split('\n')[0].substring(0, 30)}` : 'Cahoots Project',
      projectKey: 'CAHOOTS',
      jiraUrl: settings.jiraIntegration?.jiraUrl || 'https://your-domain.atlassian.net',
      apiToken: settings.jiraIntegration?.apiToken || '',
      userEmail: settings.jiraIntegration?.userEmail || '',
      accountId: null,
      users: [{ username: 'project-admin', email: settings.jiraIntegration?.userEmail || '' }]
    });
    setIsJiraModalOpen(true);
  };
  const onJiraModalClose = () => setIsJiraModalOpen(false);

  // Function to handle exporting to JIRA via backend API
  const handleExportToJira = async () => {
    if (!features.length) return;

    setIsExporting(true);

    // Close config modal and open progress modal immediately
    onJiraModalClose();
    setIsJiraProgressOpen(true);

    try {
      // Prepare the request payload
      const exportRequest = {
        config: {
          jira_url: jiraConfig.jiraUrl,
          user_email: jiraConfig.userEmail,
          api_token: jiraConfig.apiToken,
          account_id: jiraConfig.accountId,
          project_name: jiraConfig.projectName,
          project_key: jiraConfig.projectKey,
          users: jiraConfig.users
        },
        plan_id: planId,
        features: features.map(e => ({
          id: e.id, title: e.title, description: e.description || '',
          priority: e.priority || 3, scope_keywords: e.scope_keywords || [],
          stories: (e.stories || []).map(s => ({
            id: s.id, actor: s.actor, action: s.action, benefit: s.benefit || '',
            acceptance_criteria: s.acceptance_criteria || [],
            priority: s.priority || 'should_have',
          }))
        }))
      };

      // Make request to start export and get export ID
      const response = await fetch('/api/jira/start-export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || 'dev-bypass-token'}`
        },
        body: JSON.stringify(exportRequest)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Update the export ID with the real one from backend
      setCurrentExportId(result.export_id);
      
    } catch (error) {
      console.error('Error exporting to JIRA:', error);
      handleJiraExportError({ message: error.message });
    } finally {
      setIsExporting(false);
    }
  };

  // Handle successful JIRA export
  const handleJiraExportSuccess = (result) => {
    onShowToast(`Successfully created JIRA project with ${result.issues_created} issues`, 'success');
    
    // Open the project in a new tab
    if (result.project_url) {
      window.open(result.project_url, '_blank');
    }
    
    // Keep the progress modal open - user can close it manually
    // The progress modal will show completion status and allow manual closure
  };

  // Handle JIRA export error
  const handleJiraExportError = (error) => {
    onShowToast(`JIRA export failed: ${error.message || 'Unknown error'}`, 'error');
    setIsJiraProgressOpen(false);
    setCurrentExportId(null);
  };

  // Handle successful Trello export
  const handleTrelloExportSuccess = (result) => {
    onShowToast(`Successfully created Trello board with ${result.cards_created} cards`, 'success');
    
    // Open the board in a new tab
    if (result.board_url) {
      window.open(result.board_url, '_blank');
    }
    
    // Keep the progress modal open - user can close it manually
    // The progress modal will show completion status and allow manual closure
  };

  // Handle Trello export error
  const handleTrelloExportError = (error) => {
    onShowToast(`Trello export failed: ${error.message || 'Unknown error'}`, 'error');
    setIsTrelloProgressOpen(false);
    setCurrentTrelloExportId(null);
  };


  // Function to validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Function to validate project key format
  const isValidProjectKey = (key) => {
    // JIRA project key requirements: 2-10 uppercase alphanumeric characters
    const keyRegex = /^[A-Z0-9]{2,10}$/;
    return keyRegex.test(key);
  };

  // Function to validate JIRA configuration
  const validateJiraConfig = () => {
    if (!jiraConfig.projectName.trim()) {
      onShowToast('Project name is required', 'error');
      return false;
    }
    if (!jiraConfig.projectKey.trim()) {
      onShowToast('Project key is required', 'error');
      return false;
    }
    if (!isValidProjectKey(jiraConfig.projectKey)) {
      onShowToast('Project key must be 2-10 uppercase alphanumeric characters (e.g., PROJ, TEST123)', 'error');
      return false;
    }
    if (!jiraConfig.jiraUrl.trim()) {
      onShowToast('JIRA URL is required', 'error');
      return false;
    }
    if (!jiraConfig.userEmail.trim()) {
      onShowToast('Your email is required', 'error');
      return false;
    }
    if (!isValidEmail(jiraConfig.userEmail)) {
      onShowToast('Invalid email format', 'error');
      return false;
    }
    if (!jiraConfig.apiToken.trim()) {
      onShowToast('API token is required', 'error');
      return false;
    }
    // Validate URL format
    try {
      new URL(jiraConfig.jiraUrl);
    } catch {
      onShowToast('Invalid JIRA URL format', 'error');
      return false;
    }
    
    return true;
  };

  // Function to handle exporting to Trello
  const handleExportToTrello = async () => {
    if (!features.length) return;

    // Get Trello API key and token from settings
    const trelloApiKey = settings.trelloIntegration?.apiKey;
    const trelloToken = settings.trelloIntegration?.token;

    // Check if Trello credentials are available
    if (!trelloApiKey || !trelloToken) {
      onShowToast('Please add your Trello API key and token in the Settings page first.', 'warning');
      return;
    }

    // Validate board name
    if (!boardName.trim()) {
      onShowToast('Board name is required', 'error');
      return;
    }

    setIsExporting(true);

    // Close config modal and open progress modal immediately
    onTrelloModalClose();
    setIsTrelloProgressOpen(true);

    try {
      // Prepare the request payload
      const exportRequest = {
        config: {
          trello_api_key: trelloApiKey,
          trello_token: trelloToken,
          board_name: boardName || `Cahoots: ${task?.description?.substring(0, BOARD_NAME_MAX_LENGTH)}`
        },
        plan_id: planId,
        features: features.map(e => ({
          id: e.id, title: e.title, description: e.description || '',
          priority: e.priority || 3, scope_keywords: e.scope_keywords || [],
          stories: (e.stories || []).map(s => ({
            id: s.id, actor: s.actor, action: s.action, benefit: s.benefit || '',
            acceptance_criteria: s.acceptance_criteria || [],
            priority: s.priority || 'should_have',
          }))
        }))
      };

      // Make request to start export and get export ID
      const response = await fetch('/api/trello/start-export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || 'dev-bypass-token'}`
        },
        body: JSON.stringify(exportRequest)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Update the export ID with the real one from backend
      setCurrentTrelloExportId(result.export_id);
      
    } catch (error) {
      console.error('Error exporting to Trello:', error);
      handleTrelloExportError({ message: error.message });
    } finally {
      setIsExporting(false);
    }
  };

  const exportButtons = (
    <div style={inline ? styles.exportOptionsInline : styles.exportOptions}>
      <button
        style={{
          ...styles.exportOption,
          ...(settings.jiraIntegration?.enabled ? {} : styles.exportOptionDisabled)
        }}
        onClick={() => {
          if (settings.jiraIntegration?.enabled) {
            onJiraModalOpen();
            if (!inline) onClose();
          }
        }}
        onMouseEnter={(e) => {
          if (settings.jiraIntegration?.enabled) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
            e.currentTarget.style.borderColor = tokens.colors.primary[500];
          }
        }}
        onMouseLeave={(e) => {
          if (settings.jiraIntegration?.enabled) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            e.currentTarget.style.borderColor = 'var(--color-border)';
          }
        }}
        title={!settings.jiraIntegration?.enabled ? "Enable JIRA integration in Settings" : ""}
      >
        <span style={styles.exportIcon}>🔷</span>
        <div style={styles.exportOptionText}>
          <span style={styles.exportOptionTitle}>JIRA</span>
          <span style={styles.exportOptionDesc}>
            {settings.jiraIntegration?.enabled ? "Create JIRA project" : "Enable in Settings first"}
          </span>
        </div>
      </button>

      <button
        style={{
          ...styles.exportOption,
          ...(settings.trelloIntegration?.enabled ? {} : styles.exportOptionDisabled)
        }}
        onClick={() => {
          if (settings.trelloIntegration?.enabled) {
            onTrelloModalOpen();
            if (!inline) onClose();
          }
        }}
        onMouseEnter={(e) => {
          if (settings.trelloIntegration?.enabled) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
            e.currentTarget.style.borderColor = tokens.colors.primary[500];
          }
        }}
        onMouseLeave={(e) => {
          if (settings.trelloIntegration?.enabled) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            e.currentTarget.style.borderColor = 'var(--color-border)';
          }
        }}
        title={!settings.trelloIntegration?.enabled ? "Enable Trello integration in Settings" : ""}
      >
        <span style={styles.exportIcon}>📋</span>
        <div style={styles.exportOptionText}>
          <span style={styles.exportOptionTitle}>Trello</span>
          <span style={styles.exportOptionDesc}>
            {settings.trelloIntegration?.enabled ? "Create Trello board" : "Enable in Settings first"}
          </span>
        </div>
      </button>
    </div>
  );

  return (
    <>
      {/* Inline mode: just render buttons */}
      {inline && exportButtons}

      {/* Modal mode: wrap in Modal */}
      {!inline && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          title="Export Plan"
          size="sm"
        >
          {exportButtons}
        </Modal>
      )}

      {/* Export to Trello Modal */}
      <Modal
        isOpen={isTrelloModalOpen}
        onClose={onTrelloModalClose}
        title="Export to Trello"
        size="sm"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[4] }}>
          {/* Trello Connection Status */}
          {settings.trelloIntegration?.enabled && settings.trelloIntegration?.apiKey &&
           settings.trelloIntegration?.token ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[4] }}>
              <h3 style={modalStyles.sectionTitle}>Trello Connection</h3>
              <div style={modalStyles.successBox}>
                <p style={modalStyles.smallText}>
                  <strong>Using saved Trello credentials</strong><br/>
                  API Key: {settings.trelloIntegration.apiKey.substring(0, 8)}...<br/>
                  Token: {settings.trelloIntegration.token.substring(0, 8)}...
                </p>
              </div>
            </div>
          ) : (
            <div style={modalStyles.warningBox}>
              <p style={modalStyles.smallText}>
                <strong>Trello credentials not configured</strong><br/>
                Please add your Trello API key and token in the Settings page first.
              </p>
            </div>
          )}

          {/* Board Name Input */}
          <div>
            <label style={modalStyles.label}>Board Name</label>
            <input
              type="text"
              value={boardName}
              onChange={(e) => setBoardName(e.target.value)}
              placeholder="Enter board name"
              style={modalStyles.input}
              maxLength={BOARD_NAME_MAX_LENGTH}
            />
            <p style={modalStyles.hint}>Maximum {BOARD_NAME_MAX_LENGTH} characters</p>
          </div>

          {/* Export Details */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[2] }}>
            <h3 style={modalStyles.sectionTitle}>What will be created:</h3>
            {features.length > 0 ? (
              <>
                <p style={{ ...modalStyles.smallText, fontWeight: tokens.typography.fontWeight.medium }}>
                  {features.length} feature{features.length !== 1 ? 's' : ''}, {features.reduce((sum, e) => sum + (e.stories?.length || 0), 0)} stories
                </p>
                <ul style={modalStyles.detailList}>
                  <li>Trello board with your specified name</li>
                  <li>One list per feature</li>
                  <li>One card per story with acceptance criteria checklists</li>
                  <li>Priority labels (Must Have, Should Have, Could Have, Won't Have)</li>
                </ul>
              </>
            ) : (
              <p style={{ ...modalStyles.smallText, color: tokens.colors.warning[500] }}>No features to export. Generate a plan first.</p>
            )}
          </div>

          {/* Footer */}
          <div style={modalStyles.footer}>
            <button style={modalStyles.cancelBtn} onClick={onTrelloModalClose}>Cancel</button>
            <button
              style={{
                ...modalStyles.primaryBtn,
                ...((isExporting || !boardName.trim() || !settings.trelloIntegration?.apiKey || !settings.trelloIntegration?.token || !features.length)
                  ? modalStyles.disabledBtn : {}),
              }}
              onClick={handleExportToTrello}
              disabled={isExporting || !boardName.trim() || !settings.trelloIntegration?.apiKey || !settings.trelloIntegration?.token || !features.length}
            >
              {isExporting ? 'Creating Trello Board...' : 'Export to Trello'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Export to JIRA Configuration Modal */}
      <Modal
        isOpen={isJiraModalOpen}
        onClose={onJiraModalClose}
        title="Configure JIRA Export"
        size="md"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[6] }}>
          {/* JIRA Connection */}
          {settings.jiraIntegration?.enabled && settings.jiraIntegration?.jiraUrl &&
           settings.jiraIntegration?.userEmail && settings.jiraIntegration?.apiToken ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[4] }}>
              <h3 style={modalStyles.sectionTitle}>JIRA Connection</h3>
              <div style={modalStyles.successBox}>
                <p style={modalStyles.smallText}>
                  <strong>Using saved JIRA credentials:</strong><br/>
                  URL: {settings.jiraIntegration.jiraUrl}<br/>
                  Email: {settings.jiraIntegration.userEmail}
                </p>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[4] }}>
              <h3 style={modalStyles.sectionTitle}>JIRA Connection</h3>

              <div>
                <label style={modalStyles.label}>JIRA URL</label>
                <input
                  type="url"
                  value={jiraConfig.jiraUrl}
                  onChange={(e) => setJiraConfig(prev => ({ ...prev, jiraUrl: e.target.value }))}
                  placeholder="https://your-domain.atlassian.net"
                  style={modalStyles.input}
                />
              </div>

              <div style={modalStyles.grid2}>
                <div>
                  <label style={modalStyles.label}>Your Email</label>
                  <input
                    type="email"
                    value={jiraConfig.userEmail}
                    onChange={(e) => setJiraConfig(prev => ({ ...prev, userEmail: e.target.value }))}
                    placeholder="your.email@example.com"
                    style={modalStyles.input}
                  />
                </div>
                <div>
                  <label style={modalStyles.label}>API Token</label>
                  <input
                    type="password"
                    value={jiraConfig.apiToken}
                    onChange={(e) => setJiraConfig(prev => ({ ...prev, apiToken: e.target.value }))}
                    placeholder="Your JIRA API token"
                    style={modalStyles.input}
                  />
                </div>
              </div>

<div style={modalStyles.infoBox}>
                <p style={{ ...modalStyles.hint, margin: 0 }}>
                  <strong>How to get your API token:</strong><br/>
                  1. Go to <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" style={modalStyles.link}>Atlassian Account Settings</a><br/>
                  2. Click "Create API token"<br/>
                  3. Copy the token and paste it above
                </p>
              </div>
            </div>
          )}

          {/* Project Configuration */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[4] }}>
            <h3 style={modalStyles.sectionTitle}>Project Configuration</h3>
            <div style={modalStyles.grid2}>
              <div>
                <label style={modalStyles.label}>Project Name</label>
                <input
                  type="text"
                  value={jiraConfig.projectName}
                  onChange={(e) => setJiraConfig(prev => ({ ...prev, projectName: e.target.value }))}
                  placeholder="Enter project name"
                  style={modalStyles.input}
                />
              </div>
              <div>
                <label style={modalStyles.label}>Project Key</label>
                <input
                  type="text"
                  value={jiraConfig.projectKey}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 10);
                    setJiraConfig(prev => ({ ...prev, projectKey: value }));
                  }}
                  placeholder="PROJ"
                  style={{
                    ...modalStyles.input,
                    borderColor: jiraConfig.projectKey && !isValidProjectKey(jiraConfig.projectKey)
                      ? tokens.colors.error[500]
                      : undefined,
                  }}
                  maxLength="10"
                />
                <p style={modalStyles.hint}>2-10 uppercase alphanumeric characters (e.g., PROJ, TEST123)</p>
                {jiraConfig.projectKey && !isValidProjectKey(jiraConfig.projectKey) && (
                  <p style={{ ...modalStyles.hint, color: tokens.colors.error[500] }}>
                    Invalid format: use only uppercase letters and numbers
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Export Details */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[2] }}>
            <h3 style={modalStyles.sectionTitle}>What will be created:</h3>
            {features.length > 0 ? (
              <>
                <p style={{ ...modalStyles.smallText, fontWeight: tokens.typography.fontWeight.medium }}>
                  {features.length} feature{features.length !== 1 ? 's' : ''}, {features.reduce((sum, e) => sum + (e.stories?.length || 0), 0)} stories
                </p>
                <ul style={modalStyles.detailList}>
                  <li>JIRA project with your specified name and key</li>
                  <li>One Feature issue per feature</li>
                  <li>One Story issue per story, linked to its parent Feature</li>
                  <li>Priority mapping from story priorities</li>
                  <li>Acceptance criteria in story descriptions</li>
                </ul>
              </>
            ) : (
              <p style={{ ...modalStyles.smallText, color: tokens.colors.warning[500] }}>No features to export. Generate a plan first.</p>
            )}
          </div>

          {/* Footer */}
          <div style={modalStyles.footer}>
            <button style={modalStyles.cancelBtn} onClick={onJiraModalClose}>Cancel</button>
            <button
              style={{
                ...modalStyles.primaryBtn,
                ...((isExporting || !features.length) ? modalStyles.disabledBtn : {}),
              }}
              onClick={() => {
                if (validateJiraConfig()) {
                  handleExportToJira();
                }
              }}
              disabled={isExporting || !features.length}
            >
              {isExporting ? 'Creating JIRA Project...' : 'Export to JIRA'}
            </button>
          </div>
        </div>
      </Modal>

      {/* JIRA Export Progress Modal */}
      <JiraExportProgress
        isOpen={isJiraProgressOpen}
        onClose={() => setIsJiraProgressOpen(false)}
        exportId={currentExportId}
        onSuccess={handleJiraExportSuccess}
        onError={handleJiraExportError}
      />

      {/* Trello Export Progress Modal */}
      <TrelloExportProgress
        isOpen={isTrelloProgressOpen}
        onClose={() => setIsTrelloProgressOpen(false)}
        exportId={currentTrelloExportId}
        onSuccess={handleTrelloExportSuccess}
        onError={handleTrelloExportError}
      />
    </>
  );
};

const modalStyles = {
  sectionTitle: {
    margin: 0,
    fontSize: tokens.typography.fontSize.base[0],
    fontWeight: tokens.typography.fontWeight.medium,
    color: 'var(--color-text)',
  },
  label: {
    display: 'block',
    fontSize: tokens.typography.fontSize.sm[0],
    fontWeight: tokens.typography.fontWeight.medium,
    color: 'var(--color-text-muted)',
    marginBottom: tokens.spacing[1],
  },
  input: {
    width: '100%',
    padding: tokens.spacing[2],
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text)',
    backgroundColor: 'var(--color-bg-secondary)',
    border: '1px solid var(--color-border)',
    borderRadius: tokens.borderRadius.md,
    outline: 'none',
    fontFamily: tokens.typography.fontFamily.sans.join(', '),
    boxSizing: 'border-box',
  },
  hint: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted)',
    marginTop: tokens.spacing[1],
  },
  link: {
    color: tokens.colors.primary[500],
    textDecoration: 'underline',
  },
  smallText: {
    margin: 0,
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text-muted)',
  },
  successBox: {
    padding: tokens.spacing[3],
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    border: `1px solid ${tokens.colors.success[500]}`,
    borderRadius: tokens.borderRadius.md,
  },
  warningBox: {
    padding: tokens.spacing[3],
    backgroundColor: 'rgba(234, 179, 8, 0.1)',
    border: `1px solid ${tokens.colors.warning[500]}`,
    borderRadius: tokens.borderRadius.md,
  },
  infoBox: {
    padding: tokens.spacing[3],
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    border: `1px solid ${tokens.colors.info[500]}`,
    borderRadius: tokens.borderRadius.md,
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacing[4],
  },
  detailList: {
    margin: 0,
    paddingLeft: tokens.spacing[5],
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text-muted)',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[1],
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacing[2],
    paddingTop: tokens.spacing[4],
    borderTop: '1px solid var(--color-border)',
  },
  cancelBtn: {
    padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
    fontSize: tokens.typography.fontSize.sm[0],
    fontFamily: tokens.typography.fontFamily.sans.join(', '),
    color: 'var(--color-text-muted)',
    backgroundColor: 'var(--color-bg-secondary)',
    border: '1px solid var(--color-border)',
    borderRadius: tokens.borderRadius.md,
    cursor: 'pointer',
  },
  primaryBtn: {
    padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
    fontSize: tokens.typography.fontSize.sm[0],
    fontFamily: tokens.typography.fontFamily.sans.join(', '),
    color: '#fff',
    backgroundColor: tokens.colors.primary[500],
    border: 'none',
    borderRadius: tokens.borderRadius.md,
    cursor: 'pointer',
  },
  disabledBtn: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};

const styles = {
  exportOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[4],
    padding: tokens.spacing[4],
  },
  exportOptionsInline: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: tokens.spacing[4],
  },
  exportOption: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[4],
    padding: tokens.spacing[6],
    backgroundColor: 'var(--color-bg-secondary)',
    border: '2px solid var(--color-border)',
    borderRadius: tokens.borderRadius.xl,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textAlign: 'left',
    width: '100%',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  exportOptionDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    backgroundColor: 'var(--color-neutral-800)',
  },
  exportIcon: {
    fontSize: '32px',
    flexShrink: 0,
  },
  exportOptionText: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[2],
    flex: 1,
  },
  exportOptionTitle: {
    fontSize: tokens.typography.fontSize.lg[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    color: 'var(--color-text)',
  },
  exportOptionDesc: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text-muted)',
    lineHeight: tokens.typography.lineHeight.relaxed,
  },
};

export default ExportModal;